// =============================================================================
// components/voice/ConnectionQualityBadge.tsx — live network-quality pill
// (P2-CALLS-BANNER).
//
// A compact glass pill that reflects the LOCAL participant's LiveKit
// ConnectionQuality (derived by the SDK from RTCStats — RTT, jitter, packet
// loss). Excellent/Good show a signal glyph in a calm tint; Poor warns; Lost
// goes error-red. It self-hides on Excellent by default (only surfaces when the
// connection degrades — WhatsApp/X behaviour) but can be forced always-on.
//
// MUST be rendered INSIDE <LiveKitRoom> (uses useRoomContext to reach the local
// participant + its ConnectionQualityChanged event). Dark-theme only (call
// stage). Tokens only — no hardcoded hex/fontSize/radius/shadow.
// =============================================================================
import React, { useEffect, useState } from 'react';
import { StyleSheet, Text, View, ViewStyle, StyleProp } from 'react-native';
import { useRoomContext } from '@livekit/react-native';
import { ConnectionQuality, ParticipantEvent } from 'livekit-client';
import { Ionicons, IoniconName } from '../ui/Icon';
import { colorTokens, spacing, radius, typographyScale } from '../../constants/theme';

const tc = colorTokens.dark;

// One presentation per quality bucket (glyph + tint + label). Colours are pulled
// from the dark semantic token set — no new hues.
const PRESENTATION: Record<
  ConnectionQuality,
  { icon: IoniconName; color: string; label: string }
> = {
  [ConnectionQuality.Excellent]: { icon: 'cellular', color: tc.success, label: 'Strong' },
  [ConnectionQuality.Good]: { icon: 'cellular', color: tc.success, label: 'Good' },
  [ConnectionQuality.Poor]: { icon: 'cellular-outline', color: tc.warning, label: 'Weak' },
  [ConnectionQuality.Lost]: { icon: 'warning', color: tc.error, label: 'No signal' },
  [ConnectionQuality.Unknown]: { icon: 'cellular-outline', color: tc.textFaint, label: 'Connecting' },
};

export interface ConnectionQualityBadgeProps {
  /** Show even when the connection is Excellent (default false → hides when strong). */
  alwaysVisible?: boolean;
  style?: StyleProp<ViewStyle>;
}

export default function ConnectionQualityBadge({
  alwaysVisible = false,
  style,
}: ConnectionQualityBadgeProps) {
  const room = useRoomContext();
  const local = room?.localParticipant;
  const [quality, setQuality] = useState<ConnectionQuality>(
    local?.connectionQuality ?? ConnectionQuality.Unknown
  );

  // Subscribe to the SDK's per-participant quality event (fed by RTCStats).
  useEffect(() => {
    if (!local) return;
    setQuality(local.connectionQuality ?? ConnectionQuality.Unknown);
    const onChange = (q: ConnectionQuality) => setQuality(q);
    local.on(ParticipantEvent.ConnectionQualityChanged, onChange);
    return () => {
      try {
        local.off(ParticipantEvent.ConnectionQualityChanged, onChange);
      } catch {
        /* participant may already be torn down */
      }
    };
  }, [local]);

  // Only surface when the connection needs attention (unless forced on).
  if (!alwaysVisible && quality === ConnectionQuality.Excellent) return null;

  const p = PRESENTATION[quality] ?? PRESENTATION[ConnectionQuality.Unknown];

  return (
    <View
      style={[styles.pill, { borderColor: p.color + '55' }, style]}
      accessibilityRole="text"
      accessibilityLabel={`Connection quality: ${p.label}`}
    >
      <Ionicons name={p.icon} size={13} color={p.color} />
      <Text style={[styles.label, { color: p.color }]}>{p.label}</Text>
    </View>
  );
}

const styles = StyleSheet.create({
  pill: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
    paddingHorizontal: spacing.sm,
    paddingVertical: 3,
    borderRadius: radius.full,
    borderWidth: 1,
    backgroundColor: 'rgba(14,14,36,0.55)',
  },
  label: {
    ...typographyScale.caption,
    fontWeight: '700',
  },
});
